// src/pages/Community.tsx
import React from "react";
import AppShell from "../layout/AppShell";
import {
  listPostsPage,
  setReaction,
  getReaction,
  upsertPost,
  deletePost,
} from "../services/communityService";
import type { CommunityPost } from "../models/community";
import type { QueryDocumentSnapshot, DocumentData } from "firebase/firestore";
import { Link } from "react-router-dom";
import {
  MdThumbUpOffAlt,
  MdThumbUp,
  MdOutlineRecommend,
  MdRecommend,
  MdAdd,
  MdEdit,
  MdDelete,
  MdSearch,
  MdFilterList,
  MdClose,
  MdSave,
} from "react-icons/md";
import { useAuthStore } from "../stores/authStore";
import {
  getFirestore,
  collection,
  where,
  getDocs,
  query as fsQuery,
  documentId,
} from "firebase/firestore";
import ConfirmDialog from "../components/ConfirmDialog";
import { COMMUNITY_MOD_ROLES } from "../models/auth";

/* permisos */
const useCan = () => {
  const me = useAuthStore((s) => s.state.user?.uid ?? null);
  const canModerate = useAuthStore((s) => s.hasAnyRole(COMMUNITY_MOD_ROLES));
  return { me, canModerate };
};

/* helpers */
type UserBrief = { fullName?: string; email?: string };
type MyReaction = { like: boolean; recommend: boolean };

function chunk<T>(arr: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}
function authorLabel(u?: UserBrief, uid?: string) {
  if (!u) return uid ? <code>{uid.slice(0, 8)}…</code> : "—";
  const name = u.fullName ? u.fullName : "—";
  const email = u.email ? u.email : "—";
  return (
    <span>
      {name} <span className="opacity-70">• {email}</span>
    </span>
  );
}
const SearchField = React.memo(function SearchField({
  value,
  onChange,
  placeholder,
  title,
  className = "",
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  title?: string;
  className?: string;
}) {
  return (
    <div className={`relative w-full ${className}`} title={title}>
      <MdSearch
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-on-surface/60"
        aria-hidden
      />
      <input
        type="text"
        autoComplete="off"
        spellCheck={false}
        className="input-surface w-full leading-[1.2]"
        style={{ paddingLeft: 36 }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
});

/* ───────── componente ───────── */
export default function Community(): React.ReactElement {
  const [rows, setRows] = React.useState<CommunityPost[]>([]);
  const [cursor, setCursor] = React.useState<QueryDocumentSnapshot<
    DocumentData,
    DocumentData
  > | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [err, setErr] = React.useState<string | null>(null);

  const { me, canModerate } = useCan();
  const authedUser = useAuthStore((s) =>
    s.state.status === "authed" ? s.state.user : null
  );

  // búsqueda / filtros
  const [q, setQ] = React.useState("");
  const [onlyMine, setOnlyMine] = React.useState(false);

  // cache de autores y reacciones del usuario
  const [authors, setAuthors] = React.useState<Record<string, UserBrief>>({});
  const [myReacts, setMyReacts] = React.useState<Record<string, MyReaction>>(
    {}
  );

  // drawer
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<CommunityPost | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [formText, setFormText] = React.useState("");

  const canEditPost = React.useCallback(
    (p: CommunityPost) => canModerate || (me !== null && p.authorUid === me),
    [canModerate, me]
  );

  // carga paginada + autores + reacción del usuario
  const load = React.useCallback(
    async (reset = false) => {
      setLoading(true);
      try {
        const page = await listPostsPage(
          20,
          reset ? undefined : cursor ?? undefined
        );
        setRows((prev) => (reset ? page.items : [...prev, ...page.items]));
        setCursor(page.next);
        setErr(null);

        // autores faltantes
        const uids = Array.from(
          new Set(
            page.items.map((p) => p.authorUid).filter(Boolean) as string[]
          )
        ).filter((uid) => !authors[uid]);
        if (uids.length > 0) {
          const db = getFirestore();
          const col = collection(db, "users");
          const found: Record<string, UserBrief> = {};
          for (const ids of chunk(uids, 10)) {
            const snap = await getDocs(
              fsQuery(col, where(documentId(), "in", ids))
            );
            snap.forEach((doc) => {
              const d = doc.data() as any;
              const brief: UserBrief = {
                ...(typeof d.fullName === "string"
                  ? { fullName: d.fullName }
                  : {}),
                ...(typeof d.email === "string" ? { email: d.email } : {}),
              };
              found[doc.id] = brief;
            });
          }
          setAuthors((prev) => ({ ...prev, ...found }));
        }

        // reacciones del usuario (si está autenticado)
        if (authedUser) {
          const toFetch = page.items
            .map((p) => p.id)
            .filter((id) => myReacts[id] === undefined);
          if (toFetch.length > 0) {
            const entries = await Promise.all(
              toFetch.map(async (postId) => {
                const r = await getReaction(postId, authedUser.uid);
                return [
                  postId,
                  { like: !!r?.like, recommend: !!r?.recommend },
                ] as const;
              })
            );
            setMyReacts((prev) => {
              const next = { ...prev };
              for (const [id, r] of entries) next[id] = r;
              return next;
            });
          }
        }
      } catch (e) {
        setErr((e as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [cursor, authors, myReacts, authedUser]
  );

  React.useEffect(() => {
    void load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = React.useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((p) => {
      if (onlyMine && me) {
        if (p.authorUid !== me) return false;
      }
      if (!needle) return true;
      const text = (p.text ?? "").toLowerCase();
      const author = authors[p.authorUid];
      const email = (author?.email ?? "").toLowerCase();
      const name = (author?.fullName ?? "").toLowerCase();
      return (
        text.includes(needle) || email.includes(needle) || name.includes(needle)
      );
    });
  }, [rows, q, authors, onlyMine, me]);

  /* ───── CRUD post ───── */
  const openCreate = () => {
    setEditing(null);
    setFormText("");
    setDrawerOpen(true);
  };
  const openEdit = (p: CommunityPost) => {
    if (!canEditPost(p)) return;
    setEditing(p);
    setFormText(p.text ?? "");
    setDrawerOpen(true);
  };
  const submitPost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!authedUser) return;
    const text = formText.trim();
    if (!text) return;
    setBusy(true);
    try {
      const isEdit = !!editing;
      const payload = isEdit
        ? { id: editing!.id, text }
        : { authorUid: authedUser.uid, text };
      const saved = await upsertPost(payload);

      if (isEdit) {
        setRows((r) =>
          r.map((x) => (x.id === saved.id ? { ...x, ...saved, text } : x))
        );
      } else {
        setRows((r) => [
          {
            ...saved,
            text,
            counters: saved.counters ?? { like: 0, recommend: 0, comment: 0 },
          },
          ...r,
        ]);
        // asegurar autor en cache
        if (!authors[authedUser.uid]) {
          const brief: UserBrief = {};
          if (authedUser.displayName) brief.fullName = authedUser.displayName;
          if (authedUser.email) brief.email = authedUser.email;
          setAuthors((prev) => ({ ...prev, [authedUser.uid]: brief }));
        }
      }
      setDrawerOpen(false);
      setEditing(null);
      setFormText("");
    } catch (e2) {
      alert((e2 as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const onDelete = async (p: CommunityPost) => {
    if (!canEditPost(p)) return;
    const ok = window.confirm(
      "¿Eliminar este post? Esta acción no se puede deshacer."
    );
    if (!ok) return;
    try {
      await deletePost(p.id);
      setRows((r) => r.filter((x) => x.id !== p.id));
    } catch (e) {
      alert((e as Error).message);
    }
  };

  /* ───── Reacciones ───── */
  const toggleLike = async (postId: string) => {
    if (!authedUser) return;
    const prev = myReacts[postId]?.like ?? false;
    const next = !prev;
    try {
      await setReaction(postId, authedUser.uid, { like: next });
      setMyReacts((m) => ({
        ...m,
        [postId]: { like: next, recommend: m[postId]?.recommend ?? false },
      }));
      setRows((rs) =>
        rs.map((p) =>
          p.id === postId
            ? {
                ...p,
                counters: {
                  ...p.counters,
                  like: p.counters.like + (next ? 1 : -1),
                },
              }
            : p
        )
      );
    } catch (e) {
      console.error(e);
    }
  };
  const toggleRecommend = async (postId: string) => {
    if (!authedUser) return;
    const prev = myReacts[postId]?.recommend ?? false;
    const next = !prev;
    try {
      await setReaction(postId, authedUser.uid, { recommend: next });
      setMyReacts((m) => ({
        ...m,
        [postId]: { like: m[postId]?.like ?? false, recommend: next },
      }));
      setRows((rs) =>
        rs.map((p) =>
          p.id === postId
            ? {
                ...p,
                counters: {
                  ...p.counters,
                  recommend: p.counters.recommend + (next ? 1 : -1),
                },
              }
            : p
        )
      );
    } catch (e) {
      console.error(e);
    }
  };

  const [confirm, setConfirm] = React.useState<{
    postId: string | null;
    open: boolean;
  }>({ postId: null, open: false });

  return (
    <AppShell>
      <section className="card p-4">
        {/* Barra superior */}
        <div className="mb-4 grid grid-cols-1 gap-2 md:grid-cols-[auto,1fr,auto] md:items-center">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">Comunidad</h2>
            <span className="badge-tonal ml-2">{filtered.length} visibles</span>
          </div>

          <div className="flex w-full items-center gap-2 overflow-x-auto pb-1 [scrollbar-width:thin]">
            <SearchField
              value={q}
              onChange={setQ}
              placeholder="Buscar por texto / autor…"
              title="Buscar"
              className="grow min-w-[18rem]"
            />
            <div className="toolbar-tonal flex items-center gap-2 px-2 py-1">
              <MdFilterList className="text-on-surface/70" />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={onlyMine}
                  onChange={(e) => setOnlyMine(e.currentTarget.checked)}
                />
                Solo mis posts
              </label>
            </div>
            <button
              className="btn btn-outline shrink-0 whitespace-nowrap"
              onClick={() => load(true)}
              disabled={loading}
            >
              Recargar
            </button>
          </div>

          {/* Cualquier autenticado puede crear (autor propio), moderadores además pueden editar/ borrar cualquiera */}
          {!!me && (
            <div className="flex justify-end">
              <button
                className="btn btn-primary flex items-center gap-1"
                onClick={openCreate}
              >
                <MdAdd /> Nuevo
              </button>
            </div>
          )}
        </div>

        {err && <p className="mb-3 text-sm text-red-600">{err}</p>}

        {/* Lista de posts */}
        <div className="space-y-3">
          {filtered.map((p) => {
            const u = authors[p.authorUid];
            const mayEdit = canEditPost(p);
            const my = myReacts[p.id] ?? { like: false, recommend: false };
            return (
              <article
                key={p.id}
                className="rounded-xl border border-surface bg-surface p-4"
              >
                <header className="mb-2 flex flex-wrap items-center gap-2">
                  <div className="text-sm text-on-surface/70">
                    Autor: {authorLabel(u, p.authorUid)}
                  </div>

                  <div className="ml-auto flex flex-wrap items-center gap-2">
                    <Link
                      className="text-sm text-primary underline"
                      to={`/community/${p.id}`}
                    >
                      Ver detalle
                    </Link>

                    {mayEdit && (
                      <>
                        <button
                          className="btn btn-outline flex items-center gap-1 px-2 py-1"
                          onClick={() => openEdit(p)}
                          title="Editar post"
                          aria-label="Editar post"
                        >
                          <MdEdit />
                          <span className="hidden sm:inline">Editar</span>
                        </button>

                        <button
                          className="btn btn-outline flex items-center gap-1 px-2 py-1"
                          onClick={() =>
                            setConfirm({ postId: p.id, open: true })
                          }
                          title="Eliminar post"
                          aria-label="Eliminar post"
                        >
                          <MdDelete />
                          <span className="hidden sm:inline">Eliminar</span>
                        </button>
                      </>
                    )}
                  </div>
                </header>

                {p.text && <p className="mb-3 whitespace-pre-wrap">{p.text}</p>}

                <footer className="flex items-center gap-4">
                  <button
                    className="btn btn-outline flex items-center gap-1"
                    onClick={() => toggleLike(p.id)}
                    title="Me gusta"
                  >
                    {my.like ? <MdThumbUp /> : <MdThumbUpOffAlt />}
                    <span>{p.counters.like}</span>
                  </button>
                  <button
                    className="btn btn-outline flex items-center gap-1"
                    onClick={() => toggleRecommend(p.id)}
                    title="Recomendar"
                  >
                    {my.recommend ? <MdRecommend /> : <MdOutlineRecommend />}
                    <span>{p.counters.recommend}</span>
                  </button>
                  <div className="ml-auto text-sm text-on-surface/70">
                    Comentarios: {p.counters.comment}
                  </div>
                </footer>
              </article>
            );
          })}
        </div>

        {/* Paginación */}
        <div className="mt-4 flex items-center gap-3">
          <button
            className="btn btn-outline"
            onClick={() => load(false)}
            disabled={loading || !cursor}
          >
            {loading ? "Cargando…" : cursor ? "Cargar más" : "No hay más"}
          </button>
        </div>
      </section>

      {/* Drawer crear/editar */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => !busy && setDrawerOpen(false)}
          />
          <aside className="absolute right-0 top-0 h-full w-full max-w-xl bg-surface shadow-xl md:rounded-l-2xl">
            <form
              className="flex h-full flex-col overflow-hidden"
              onSubmit={submitPost}
            >
              {/* Header */}
              <div className="flex items-center justify-between border-b border-surface p-4">
                <h3 className="text-lg font-semibold">
                  {editing ? "Editar post" : "Nuevo post"}
                </h3>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setDrawerOpen(false)}
                  disabled={busy}
                  aria-label="Cerrar"
                >
                  <MdClose />
                </button>
              </div>

              {/* Contenido scrollable */}
              <div className="grow overflow-y-auto p-4 pb-28">
                <div className="space-y-1">
                  <label className="block text-xs text-on-surface/70">
                    Contenido
                  </label>
                  <textarea
                    className="input-surface min-h-[140px] md:min-h-[200px]"
                    value={formText}
                    onChange={(e) => setFormText(e.currentTarget.value)}
                    placeholder="Escribe el texto del post…"
                  />
                </div>
              </div>

              {/* Barra inferior fija (siempre visible) */}
              <div
                className="fixed bottom-0 left-0 right-0 z-50 border-t border-surface bg-surface/95 px-4 py-2 backdrop-blur md:static md:px-4"
                style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
              >
                <div className="flex items-center gap-2">
                  <button
                    type="submit"
                    className="btn btn-primary flex items-center gap-1"
                    disabled={busy}
                  >
                    <MdSave /> {busy ? "Guardando…" : "Guardar"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => setDrawerOpen(false)}
                    disabled={busy}
                  >
                    <MdClose /> Cancelar
                  </button>
                </div>
              </div>
            </form>
          </aside>
        </div>
      )}
      <ConfirmDialog
        open={confirm.open}
        title="Eliminar post"
        message="Esta acción no se puede deshacer."
        onCancel={() => setConfirm({ postId: null, open: false })}
        onConfirm={async () => {
          const id = confirm.postId!;
          setConfirm({ postId: null, open: false });
          try {
            await deletePost(id);
            setRows((r) => r.filter((x) => x.id !== id));
          } catch (e) {
            alert((e as Error).message);
          }
        }}
      />
    </AppShell>
  );
}
